import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/FirebaseAuthContext';

const Connections = () => {
  const { user } = useAuth();
  const [connections, setConnections] = useState({
    gmail: false,
    outlook: false
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch existing connections when component mounts
  useEffect(() => {
    const fetchConnections = async () => {
      try {
        // Replace with your actual API endpoint
        const response = await fetch(`/api/connections/${user.uid}`);
        const data = await response.json();
        setConnections({
          gmail: data.gmail || false,
          outlook: data.outlook || false
        });
      } catch (err) {
        setError('Failed to fetch connection status');
        console.error('Error fetching connections:', err);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchConnections();
    }
  }, [user]);

  const handleGmailConnect = async () => {
    try {
      // Replace with your actual Gmail OAuth endpoint
      window.location.href = '/api/auth/gmail';
    } catch (err) {
      setError('Failed to connect to Gmail');
      console.error('Gmail connection error:', err);
    }
  };

  const handleOutlookConnect = async () => {
    try {
      // Replace with your actual Outlook OAuth endpoint
      window.location.href = '/api/auth/outlook';
    } catch (err) {
      setError('Failed to connect to Outlook');
      console.error('Outlook connection error:', err);
    }
  };

  const handleDisconnect = async (service) => {
    try {
      // Replace with your actual disconnect endpoint
      await fetch(`/api/connections/${user.uid}/${service}`, {
        method: 'DELETE'
      });
      setConnections(prev => ({
        ...prev,
        [service]: false
      }));
    } catch (err) {
      setError(`Failed to disconnect ${service}`);
      console.error('Disconnect error:', err);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-8">Email Connections</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Gmail Connection Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <img
                src="/gmail-icon.png"
                alt="Gmail"
                className="w-8 h-8 mr-3"
              />
              <h2 className="text-xl font-semibold">Gmail</h2>
            </div>
            <div className={`px-3 py-1 rounded-full text-sm ${
              connections.gmail ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            }`}>
              {connections.gmail ? 'Connected' : 'Not Connected'}
            </div>
          </div>
          <p className="text-gray-600 mb-4">
            Connect your Gmail account to track emails and responses.
          </p>
          <button
            onClick={connections.gmail ? () => handleDisconnect('gmail') : handleGmailConnect}
            className={`w-full py-2 px-4 rounded-md ${
              connections.gmail
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {connections.gmail ? 'Disconnect' : 'Connect Gmail'}
          </button>
        </div>

        {/* Outlook Connection Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <img
                src="/outlook-icon.png"
                alt="Outlook"
                className="w-8 h-8 mr-3"
              />
              <h2 className="text-xl font-semibold">Outlook</h2>
            </div>
            <div className={`px-3 py-1 rounded-full text-sm ${
              connections.outlook ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            }`}>
              {connections.outlook ? 'Connected' : 'Not Connected'}
            </div>
          </div>
          <p className="text-gray-600 mb-4">
            Connect your Outlook account to track emails and responses.
          </p>
          <button
            onClick={connections.outlook ? () => handleDisconnect('outlook') : handleOutlookConnect}
            className={`w-full py-2 px-4 rounded-md ${
              connections.outlook
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {connections.outlook ? 'Disconnect' : 'Connect Outlook'}
          </button>
        </div>
      </div>

      <div className="mt-8 bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-2">About Email Tracking</h3>
        <p className="text-gray-600">
          Connecting your email accounts allows our service to:
          <ul className="list-disc ml-6 mt-2">
            <li>Track email opens and responses</li>
            <li>Monitor email delivery status</li>
            <li>Analyze response times and patterns</li>
            <li>Generate reports and insights</li>
          </ul>
        </p>
        <p className="text-sm text-gray-500 mt-4">
          We only track emails that are explicitly marked for tracking. Your personal emails remain private.
        </p>
      </div>
    </div>
  );
};

export default Connections; 